import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Table,
  Stack,
  Checkbox,
  TableRow,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  tableCellClasses,
  Dialog,
  IconButton,
  DialogActions,
  DialogTitle,
  DialogContent,
  Grid,
  Select,
  MenuItem,
  Button
} from '@mui/material';
import { Box, styled } from '@mui/system';
import { format } from 'date-fns';
import CloseIcon from '@mui/icons-material/Close';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useDispatch } from 'react-redux';
import { PayableApi } from '../../utils/Apis';
import { downloadApiData } from '../../store/Slice/Global/GlobalSlice';
import usePayableSelector from '../../store/Slice/Payable/PayableSelector';
// components
import Page from '../../components/Page';
import BrudCrumbs from '../../components/BreadCrumbs';
import { getPayableListData } from '../../store/Slice/Payable/PayableSlice';
import Scrollbar from '../../components/Scrollbar';
import SearchNotFound from '../../components/SearchNotFound';
import { UserMoreMenu } from '../../components/_dashboard/user';
import { fDate } from '../../utils/formatTime';
import { fIndianAmount } from '../../utils/formatNumber';
import { DateRangeStyled } from '../Global/Styling';
import PayableToolbar from './PayableToolbar';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import PayableDialogRedux from './PayableInvoicePaidDialog';
import useDebounce from '../../utils/useDebounce';
import { NOT_AVAILABLE } from '../Global/Constants';

// ----------------------------------------------------------------------
const TABLE_HEAD = [
  { id: 'fileNo', label: 'FILE NO', sortable: true },
  { id: 'borrowerName', label: 'BORROWER NAME', sortable: true },
  { id: 'bankName', label: 'BANK NAME', sortable: true },
  { id: 'branchName', label: 'BRANCH NAME', sortable: true },
  { id: 'loanName', label: 'LOAN NAME', sortable: true },
  { id: 'partnerName', label: 'PARTNER NAME', sortable: true },
  { id: 'invoiceNo', label: 'INVOICE NO', sortable: true },
  { id: 'invoiceAmount', label: 'INVOICE AMOUNT', sortable: true },
  { id: 'partnerPayout', label: 'TOTAL AMOUNT', sortable: true },
  { id: 'payableStatus', label: 'STATUS', sortable: true },
  { id: 'action', label: 'ACTION', sortable: false }
];

const StyledHeadCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    fontSize: 11.5,
    fontWeight: 700,
    padding: '12px 14px',
    backgroundColor: '#ffffff',
    color: '#64748b',
    borderBottom: '1px solid #e2e8f0',
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap'
  }
}));

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.body}`]: {
    fontSize: 13,
    padding: '12px 14px',
    color: '#334155',
    borderBottom: '1px solid #f1f5f9',
    whiteSpace: 'nowrap'
  }
}));

const getStatusBadgeStyles = (status) => {
  const s = String(status || '').toUpperCase();
  if (s === 'PAID') {
    return { bgcolor: '#dcfce7', color: '#16a34a' };
  }
  if (s === 'PENDING') {
    return { bgcolor: '#fee2e2', color: '#dc2626' };
  }
  return { bgcolor: '#fef3c7', color: '#d97706' };
};

export default function Payable() {
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('desc');
  const [selected, setSelected] = useState([]);
  const [orderBy, setOrderBy] = useState('borrowerName');
  const [filterName, setFilterName] = useState('');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [openPayment, setOpenPayment] = useState(false);
  const [selectedPaybleId, setSelectedPaybleId] = useState(null);
  const [selectedInvoiceAmount, setSelectedInvoiceAmount] = useState('');
  const [downloadDialog, setDownloadDialog] = useState(false);
  const [filterState, setFilterState] = useState({
    partnerId: null,
    bankId: null,
    invoiceNo: null,
    status: null,
    fromDate: null,
    toDate: null
  });
  const [filterDatesRange, setFilterDatesRange] = useState([
    {
      startDate: null,
      endDate: null,
      key: 'selection'
    }
  ]);

  const { LoginData } = useLoginSelector();
  const dispatch = useDispatch();
  const { PayableListData } = usePayableSelector();
  const debouncedSearch = useDebounce(filterName, 600);

  const handleRequestSort = (property) => {
    if (property !== 'action') {
      const isAsc = orderBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setOrderBy(property);
    }
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = PayableListData?.map((n) => n.paybleId);
      setSelected(newSelecteds || []);
      return;
    }
    setSelected([]);
  };

  const handleDateChange = (ranges) => {
    const { selection } = ranges;
    setFilterDatesRange([selection]);
    setFilterState((prev) => ({
      ...prev,
      fromDate: selection.startDate ? format(selection.startDate, 'yyyy-MM-dd') : null,
      toDate: selection.endDate ? format(selection.endDate, 'yyyy-MM-dd') : null
    }));
  };

  const handleFilterByName = (event) => {
    setFilterName(event.target.value);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const checkDownload = () => {
    setDownloadDialog(true);
  };

  const handleDownload = async (id) => {
    let payableValue;
    if (id) {
      payableValue = [id];
    } else {
      payableValue = [];
    }
    const credentials = {
      userId: LoginData?.userId,
      companyId: LoginData?.companyId,
      paybleIds: selected.length > 0 ? selected : payableValue,
      filterModel: {
        partnerId: filterState?.partnerId,
        bankId: filterState?.bankId,
        invoiceNo: filterState?.invoiceNo,
        status: filterState?.status,
        fromDate: filterState?.fromDate,
        toDate: filterState?.toDate
      }
    };

    try {
      const result = await dispatch(downloadApiData(PayableApi?.payableDownload, credentials));

      if (result?.success && result?.data) {
        let fileName;
        if (selected.length === 1 || id) {
          fileName = `PayableDataDownload_${format(new Date(), 'dd-MM-yyyy_HH-mm-ss')}.pdf`;
        } else {
          fileName = `PayableDataDownload_${format(new Date(), 'dd-MM-yyyy_HH-mm-ss')}.zip`;
        }

        const url = window.URL.createObjectURL(result.data);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        setDownloadDialog(false);
      } else {
        console.error('Download failed:', result?.error);
      }
    } catch (error) {
      console.error('Export error:', error);
    }
  };

  const getBankListApi = useCallback(() => {
    const credentials = {
      companyId: LoginData?.companyId,
      userId: LoginData?.userId,
      filterModel: {
        partnerId: filterState.partnerId || null,
        bankId: filterState.bankId || null,
        invoiceNo: filterState.invoiceNo || null,
        fromDate: filterState.fromDate || null,
        toDate: filterState.toDate || null
      },
      entityListRequestModel: {
        sortOrder: order || 'desc',
        pageSize: rowsPerPage,
        pageNumber: page + 1,
        searchLiteral: debouncedSearch?.length >= 2 ? debouncedSearch : null
      }
    };

    dispatch(getPayableListData(credentials));
  }, [LoginData, filterState, order, page, rowsPerPage, debouncedSearch, dispatch]);

  useEffect(() => {
    getBankListApi();
  }, [getBankListApi]);

  useEffect(() => {
    if (!PayableListData) {
      setHasNextPage(true);
      return;
    }
    setHasNextPage(PayableListData.length >= rowsPerPage);
  }, [PayableListData, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, filterState]);

  const filteredUsers = PayableListData || [];
  const isUserNotFound = filteredUsers.length === 0;
  const totalCount = filteredUsers.length;
  const startRecord = totalCount > 0 ? page * rowsPerPage + 1 : 0;
  const endRecord = totalCount > 0 ? page * rowsPerPage + totalCount : 0;

  return (
    <Page title="Partner Management System || Payable">
      <Container maxWidth="xl" sx={{ pb: 6, pt: 2 }}>
        {/* Top Header Row with Icon, Title and Breadcrumbs */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          sx={{ mb: 3.5, gap: 2 }}
        >
          {/* Left Title with Icon */}
          <Stack direction="row" spacing={2} alignItems="center">
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '12px',
                bgcolor: '#e0f2fe',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#0284c7'
              }}
            >
              <ReceiptLongOutlinedIcon sx={{ fontSize: 26 }} />
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 700,
                  color: '#0f172a',
                  fontSize: { xs: '20px', sm: '24px' },
                  letterSpacing: '-0.3px',
                  mb: 0.25
                }}
              >
                Payable
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontSize: '13px' }}>
                Track and manage partner payout invoices, payments, and payable records.
              </Typography>
            </Box>
          </Stack>

          {/* Right Breadcrumbs */}
          <Box>
            <BrudCrumbs />
          </Box>
        </Stack>

        {/* Main Content Card */}
        <Card
          sx={{
            borderRadius: '16px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
            overflow: 'hidden',
            bgcolor: '#ffffff'
          }}
        >
          <PayableToolbar
            numSelected={selected.length}
            filterName={filterName}
            onFilterName={handleFilterByName}
            parent="payable"
            selected={selected}
            handleDownload={selected.length === 0 ? checkDownload : handleDownload}
            filterParameter={filterState}
            setFilterParameter={setFilterState}
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 900, minHeight: 380 }}>
              <Table>
                <thead>
                  <TableRow>
                    <StyledHeadCell padding="checkbox" sx={{ pl: 2.5 }}>
                      <Checkbox
                        indeterminate={
                          selected.length > 0 && selected.length < (PayableListData?.length || 0)
                        }
                        checked={
                          PayableListData?.length > 0 && selected.length === PayableListData?.length
                        }
                        onChange={handleSelectAllClick}
                        sx={{
                          color: '#cbd5e1',
                          '&.Mui-checked, &.MuiCheckbox-indeterminate': { color: '#0284c7' }
                        }}
                      />
                    </StyledHeadCell>
                    {TABLE_HEAD.map((headCell) => (
                      <StyledHeadCell
                        key={headCell.id}
                        onClick={() => headCell.sortable && handleRequestSort(headCell.id)}
                        sx={{
                          cursor: headCell.sortable ? 'pointer' : 'default',
                          userSelect: 'none',
                          ...(headCell.id === 'action' ? { textAlign: 'center' } : {})
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent={headCell.id === 'action' ? 'center' : 'flex-start'}
                          spacing={0.5}
                        >
                          <span>{headCell.label}</span>
                          {headCell.sortable && (
                            <SwapVertIcon
                              sx={{
                                fontSize: 16,
                                color: orderBy === headCell.id ? '#0284c7' : '#94a3b8'
                              }}
                            />
                          )}
                        </Stack>
                      </StyledHeadCell>
                    ))}
                  </TableRow>
                </thead>
                <TableBody>
                  {filteredUsers.map((row, index) => {
                    const {
                      paybleId,
                      fileNo,
                      borrowerName,
                      bankName,
                      branchName,
                      loanName,
                      partnerName,
                      invoiceNo,
                      invoiceAmount,
                      partnerPayout,
                      payableStatus
                    } = row;
                    const isItemSelected = selected.indexOf(paybleId) !== -1;
                    const badgeStyles = getStatusBadgeStyles(payableStatus);

                    return (
                      <TableRow
                        hover
                        key={paybleId || index}
                        tabIndex={-1}
                        role="checkbox"
                        selected={isItemSelected}
                        aria-checked={isItemSelected}
                        sx={{
                          '&:hover': { backgroundColor: '#f8fafc !important' },
                          '&.Mui-selected, &.Mui-selected:hover': {
                            backgroundColor: 'rgba(2, 132, 199, 0.06)'
                          }
                        }}
                      >
                        <StyledTableCell padding="checkbox" sx={{ pl: 2.5 }}>
                          <Checkbox
                            checked={isItemSelected}
                            disabled={row?.partnerId === 0}
                            onChange={(event) => handleClick(event, paybleId)}
                            sx={{
                              color: '#cbd5e1',
                              '&.Mui-checked': { color: '#0284c7' }
                            }}
                          />
                        </StyledTableCell>

                        <StyledTableCell sx={{ fontWeight: 600, color: '#0284c7' }}>
                          {fileNo || NOT_AVAILABLE}
                        </StyledTableCell>
                        <StyledTableCell sx={{ fontWeight: 600, color: '#1e293b' }}>
                          {borrowerName || NOT_AVAILABLE}
                        </StyledTableCell>
                        <StyledTableCell sx={{ color: '#475569' }}>
                          {bankName || NOT_AVAILABLE}
                        </StyledTableCell>
                        <StyledTableCell sx={{ color: '#475569' }}>
                          {branchName || NOT_AVAILABLE}
                        </StyledTableCell>
                        <StyledTableCell sx={{ color: '#475569' }}>
                          {loanName || NOT_AVAILABLE}
                        </StyledTableCell>
                        <StyledTableCell sx={{ color: '#475569' }}>
                          {partnerName || NOT_AVAILABLE}
                        </StyledTableCell>
                        <StyledTableCell sx={{ color: '#475569' }}>
                          {invoiceNo || NOT_AVAILABLE}
                        </StyledTableCell>
                        <StyledTableCell sx={{ fontWeight: 700, color: '#0f172a' }}>
                          {fIndianAmount(invoiceAmount) ?? NOT_AVAILABLE}
                        </StyledTableCell>
                        <StyledTableCell sx={{ fontWeight: 700, color: '#0284c7' }}>
                          {fIndianAmount(partnerPayout) ?? NOT_AVAILABLE}
                        </StyledTableCell>
                        <StyledTableCell>
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              px: 1.5,
                              py: 0.35,
                              borderRadius: '20px',
                              fontSize: 12,
                              fontWeight: 600,
                              textTransform: 'capitalize',
                              bgcolor: badgeStyles.bgcolor,
                              color: badgeStyles.color
                            }}
                          >
                            {payableStatus || NOT_AVAILABLE}
                          </Box>
                        </StyledTableCell>

                        <StyledTableCell align="center" width={100}>
                          <UserMoreMenu
                            rowId={row}
                            parent="payable"
                            handlePaymentAction={() => {
                              setSelectedPaybleId(row.paybleId);
                              setSelectedInvoiceAmount(row.invoiceAmount);
                              setOpenPayment(true);
                            }}
                            selected={selected}
                            onPayClick={handleDownload}
                          />
                        </StyledTableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
                {isUserNotFound && (
                  <TableBody>
                    <TableRow>
                      <StyledTableCell
                        align="center"
                        colSpan={TABLE_HEAD.length + 1}
                        sx={{ py: 6 }}
                      >
                        <SearchNotFound searchQuery={filterName} />
                      </StyledTableCell>
                    </TableRow>
                  </TableBody>
                )}
              </Table>
            </TableContainer>
          </Scrollbar>

          {/* Pagination Footer */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 3,
              py: 2,
              borderTop: '1px solid #f1f5f9',
              gap: 2
            }}
          >
            <Typography sx={{ fontSize: 13, color: '#64748b' }}>
              Showing{' '}
              <span style={{ fontWeight: 600, color: '#334155' }}>
                {startRecord} to {endRecord}
              </span>{' '}
              records
            </Typography>

            <Stack direction="row" spacing={2} alignItems="center">
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography sx={{ fontSize: 13, color: '#64748b' }}>Rows per page</Typography>
                <Select
                  size="small"
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(Number(e.target.value));
                    setPage(0);
                  }}
                  sx={{
                    height: 32,
                    fontSize: 13,
                    borderRadius: '6px',
                    '& fieldset': { borderColor: '#e2e8f0' }
                  }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={25}>25</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                </Select>
              </Stack>

              <Stack direction="row" spacing={0.5} alignItems="center">
                <IconButton
                  size="small"
                  disabled={page === 0}
                  onClick={() => setPage((prev) => Math.max(0, prev - 1))}
                  sx={{
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0',
                    width: 32,
                    height: 32,
                    color: '#64748b',
                    '&.Mui-disabled': { borderColor: '#f1f5f9', color: '#cbd5e1' }
                  }}
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>

                <Box
                  sx={{
                    minWidth: 32,
                    height: 32,
                    borderRadius: '6px',
                    bgcolor: '#0284c7',
                    color: '#ffffff',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 13,
                    fontWeight: 600,
                    px: 1
                  }}
                >
                  {page + 1}
                </Box>

                <IconButton
                  size="small"
                  disabled={totalCount < rowsPerPage}
                  onClick={() => setPage((prev) => prev + 1)}
                  sx={{
                    borderRadius: '6px',
                    border: '1px solid #e2e8f0',
                    width: 32,
                    height: 32,
                    color: '#64748b',
                    '&.Mui-disabled': { borderColor: '#f1f5f9', color: '#cbd5e1' }
                  }}
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Stack>
            </Stack>
          </Box>
        </Card>

        {/* Payment Dialog */}
        <PayableDialogRedux
          openPayment={openPayment}
          setOpenPayment={setOpenPayment}
          userId={LoginData?.userId}
          paybleId={selectedPaybleId}
          invoiceAmount={selectedInvoiceAmount}
          onSuccess={() => {
            getBankListApi();
          }}
        />

        {/* Download Modal Dialog */}
        <Dialog
          open={downloadDialog}
          onClose={() => setDownloadDialog(false)}
          fullWidth
          maxWidth="xs"
          PaperProps={{
            sx: { borderRadius: '16px', overflow: 'hidden' }
          }}
        >
          <DialogTitle
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              py: 2,
              px: 3,
              bgcolor: '#f8fafc',
              borderBottom: '1px solid #e2e8f0'
            }}
          >
            <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
              Download Invoice
            </Typography>
            <IconButton
              size="small"
              onClick={() => setDownloadDialog(false)}
              sx={{ color: '#64748b' }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sx={{ position: 'relative' }}>
                <Box className="displayDateRange">
                  {filterDatesRange[0].startDate !== null
                    ? `${fDate(filterDatesRange[0].startDate)} to `
                    : ''}
                  {filterDatesRange[0].endDate !== null ? fDate(filterDatesRange[0].endDate) : ''}
                  {filterDatesRange[0].startDate === null &&
                  filterDatesRange[0].endDate === null ? (
                    <Typography className="dissabledMenu">Select Date Range</Typography>
                  ) : null}
                </Box>
                <Select
                  fullWidth
                  inputProps={{
                    className: 'textInput'
                  }}
                  MenuProps={{
                    className: 'menuOpened'
                  }}
                  value=" "
                  className="datesSelect"
                  sx={{
                    bgcolor: '#ffffff',
                    borderRadius: '8px',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' }
                  }}
                >
                  <DateRangeStyled
                    editableDateInputs={false}
                    onChange={handleDateChange}
                    moveRangeOnFirstSelection={false}
                    ranges={filterDatesRange}
                    className="dateRangePicker"
                  />
                </Select>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions
            sx={{ p: 2, px: 3, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0', gap: 1 }}
          >
            <Button
              onClick={() => setDownloadDialog(false)}
              variant="outlined"
              sx={{
                borderRadius: '8px',
                px: 3,
                py: 0.9,
                textTransform: 'none',
                fontWeight: 600,
                color: '#64748b',
                borderColor: '#cbd5e1',
                '&:hover': { bgcolor: '#f1f5f9' }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={() => handleDownload()}
              variant="contained"
              sx={{
                borderRadius: '8px',
                px: 3,
                py: 0.9,
                bgcolor: '#0284c7',
                color: '#ffffff',
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: '0 4px 12px rgba(2, 132, 199, 0.25)',
                '&:hover': { bgcolor: '#0369a1' }
              }}
            >
              Submit
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Page>
  );
}
